import { useLayoutEffect, useRef, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { MessageContent } from "./MessageContent";
import { UserMessageAttachments } from "./UserMessageAttachments";

const BUBBLE_PADDING_Y = 16;
const TEXT_LINE_HEIGHT = 16;
const SINGLE_LINE_MAX = BUBBLE_PADDING_Y * 2 + TEXT_LINE_HEIGHT + 1;

export function UserMessageBubble({
  text,
  isDictated,
  attachments,
  userColor,
}: {
  text: string;
  isDictated: boolean;
  attachments: ChatEntryWithSubTables["attachments"];
  userColor: string | null;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [singleLine, setSingleLine] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (height: number) =>
      setSingleLine(height <= SINGLE_LINE_MAX);
    measure(el.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        measure(
          entry.borderBoxSize?.[0]?.blockSize ??
            entry.target.getBoundingClientRect().height
        );
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: userColor ? `var(--${userColor}-200)` : "var(--alpha4)",
        color: userColor ? `var(--${userColor}-1000)` : undefined,
        padding: BUBBLE_PADDING_Y,
        borderRadius: singleLine ? 9999 : 16,
        minWidth: 0,
      }}
    >
      <UserMessageAttachments attachments={attachments} />
      <MessageContent text={text} isDictated={isDictated} />
    </div>
  );
}
